import { useState, useRef, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { BookOpen, Link2, Send, Wrench } from 'lucide-react'
import { api } from '@/lib/api'
import { cn } from '@/lib/utils'

type Msg = { role: 'user' | 'assistant'; content: string }
type ToolCall = {
  id: string
  name: string
  input: Record<string, unknown>
  summary?: string
  sources?: Array<{ id: string; title: string }>
  state: 'running' | 'done'
}

const TOOL_DESCRIPTIONS: Record<string, string> = {
  search_articles: 'Search the content library',
  get_article: 'Fetch an article',
  list_pending_approvals: 'List pending approvals',
  get_seo_snapshot: 'Pull the 28-day SEO snapshot',
}

export function AgentChatPage() {
  const { agentId = '' } = useParams()
  const { data: agents = [] } = useQuery({ queryKey: ['agents'], queryFn: api.agents })
  const agent = agents.find((a) => a.id === agentId)

  const [messages, setMessages] = useState<Msg[]>([])
  const [input, setInput] = useState('')
  const [streaming, setStreaming] = useState(false)
  const [toolCalls, setToolCalls] = useState<ToolCall[]>([])
  const endRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  const sources = Array.from(
    new Map(
      toolCalls.flatMap((t) => t.sources ?? []).map((s) => [s.id, s]),
    ).values(),
  )

  async function send() {
    const text = input.trim()
    if (!text || streaming) return
    setInput('')
    const next: Msg[] = [
      ...messages,
      { role: 'user', content: text },
      { role: 'assistant', content: '' },
    ]
    setMessages(next)
    setToolCalls([])
    setStreaming(true)

    try {
      const res = await fetch(`/api/agents/${agentId}/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ messages: next.slice(0, -1) }),
      })
      if (!res.body) throw new Error('no stream')

      const reader = res.body.getReader()
      const decoder = new TextDecoder()
      let buffer = ''

      while (true) {
        const { value, done } = await reader.read()
        if (done) break
        buffer += decoder.decode(value, { stream: true })
        const parts = buffer.split('\n\n')
        buffer = parts.pop() ?? ''
        for (const part of parts) {
          if (!part.startsWith('data: ')) continue
          const json = part.slice(6).trim()
          if (json === '[DONE]') continue
          try {
            const evt = JSON.parse(json) as {
              delta?: string
              error?: string
              tool_use?: { id: string; name: string; input: Record<string, unknown> }
              tool_result?: {
                id: string
                name: string
                summary: string
                sources: Array<{ id: string; title: string }>
              }
            }
            if (evt.delta) {
              setMessages((prev) => {
                const copy = [...prev]
                const last = copy[copy.length - 1]
                copy[copy.length - 1] = { ...last, content: last.content + evt.delta }
                return copy
              })
            } else if (evt.tool_use) {
              const tu = evt.tool_use
              setToolCalls((prev) => [
                ...prev,
                { id: tu.id, name: tu.name, input: tu.input, state: 'running' },
              ])
            } else if (evt.tool_result) {
              const tr = evt.tool_result
              setToolCalls((prev) =>
                prev.map((t) =>
                  t.id === tr.id
                    ? { ...t, state: 'done', summary: tr.summary, sources: tr.sources }
                    : t,
                ),
              )
            } else if (evt.error) {
              setMessages((prev) => {
                const copy = [...prev]
                copy[copy.length - 1] = { role: 'assistant', content: `(error: ${evt.error})` }
                return copy
              })
            }
          } catch {
            /* ignore non-JSON frames */
          }
        }
      }
    } catch (err) {
      setMessages((prev) => {
        const copy = [...prev]
        copy[copy.length - 1] = { role: 'assistant', content: `(error: ${String(err)})` }
        return copy
      })
    } finally {
      setStreaming(false)
    }
  }

  return (
    <div className="h-full grid grid-cols-1 md:grid-cols-[1fr_300px] min-w-0">
      <div className="flex flex-col min-w-0 md:border-r border-border">
        <header className="px-4 md:px-5 py-3 md:py-4 border-b border-border flex items-center justify-between">
          <div>
            <div className="text-lg font-medium">{agent?.name ?? agentId}</div>
            <div className="text-xs text-ink-secondary">
              {agent?.role ? `Role: ${agent.role}` : ''} · Model: claude-sonnet-4-6
            </div>
          </div>
          <Link to="/agents" className="text-xs text-ink-tertiary hover:text-ink-primary">
            ← all agents
          </Link>
        </header>

        <div className="flex-1 overflow-auto px-5 py-4 flex flex-col gap-3">
          {messages.length === 0 && (
            <div className="text-sm text-ink-tertiary text-center py-12">
              Start a conversation with the {agent?.name ?? 'agent'}. Try: "what's in the approval queue?" or
              "find articles about walleye."
            </div>
          )}
          {messages.map((m, i) => (
            <div
              key={i}
              className={cn(
                'rounded-md px-3 py-2 text-sm max-w-[85%] whitespace-pre-wrap',
                m.role === 'user'
                  ? 'bg-brand-50 text-brand-900 self-end'
                  : 'bg-surface-card border border-border self-start',
              )}
            >
              {m.content || <span className="text-ink-tertiary">…</span>}
            </div>
          ))}
          <div ref={endRef} />
        </div>

        <div className="px-5 pb-5">
          <div className="flex gap-2 border border-border rounded-md bg-surface-card p-2">
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault()
                  send()
                }
              }}
              placeholder={`Message ${agent?.name ?? 'agent'}…`}
              className="flex-1 min-w-0 bg-transparent outline-none text-sm px-2"
              disabled={streaming}
            />
            <button
              onClick={send}
              disabled={streaming || !input.trim()}
              className="bg-brand-600 text-white rounded-md px-3 py-1 text-sm disabled:opacity-60"
            >
              <Send className="w-3.5 h-3.5 inline -mt-0.5 mr-1" /> Send
            </button>
          </div>
        </div>
      </div>

      <aside className="hidden md:flex bg-surface-muted/40 overflow-auto p-4 flex-col gap-4">
        <section>
          <div className="flex items-center gap-1.5 text-xs font-medium text-ink-secondary mb-2">
            <Wrench size={12} /> Tools called
          </div>
          {toolCalls.length === 0 ? (
            <p className="text-[11px] text-ink-tertiary italic">
              The agent hasn't reached for a tool yet.
            </p>
          ) : (
            <div className="flex flex-col gap-2">
              {toolCalls.map((t) => (
                <div
                  key={t.id}
                  className="border border-border rounded-md bg-surface-card p-2.5 text-[11px]"
                >
                  <div className="flex items-center justify-between gap-2">
                    <code className="font-mono text-brand-600 text-[11px]">{t.name}</code>
                    <span
                      className={cn(
                        'text-[10px] px-1.5 py-0.5 rounded',
                        t.state === 'running'
                          ? 'bg-status-review-bg text-status-review-fg'
                          : 'bg-status-approved-bg text-status-approved-fg',
                      )}
                    >
                      {t.state === 'running' ? 'running…' : 'done'}
                    </span>
                  </div>
                  <div className="text-ink-tertiary mt-1">{TOOL_DESCRIPTIONS[t.name] ?? ''}</div>
                  {Object.keys(t.input).length > 0 && (
                    <div className="mt-1 font-mono text-ink-secondary break-all">
                      {Object.entries(t.input)
                        .map(([k, v]) => `${k}=${JSON.stringify(v)}`)
                        .join(' ')}
                    </div>
                  )}
                  {t.summary && <div className="mt-1 text-ink-primary">→ {t.summary}</div>}
                </div>
              ))}
            </div>
          )}
        </section>

        <section>
          <div className="flex items-center gap-1.5 text-xs font-medium text-ink-secondary mb-2">
            <BookOpen size={12} /> Sources
          </div>
          {sources.length === 0 ? (
            <p className="text-[11px] text-ink-tertiary italic">No sources referenced yet.</p>
          ) : (
            <div className="flex flex-col gap-1">
              {sources.map((s) => (
                <div
                  key={s.id}
                  className="border border-border rounded-md bg-surface-card px-2 py-1.5 text-[11px]"
                >
                  <div className="flex items-start gap-1.5">
                    <Link2 size={11} className="mt-0.5 shrink-0 text-ink-tertiary" />
                    <div className="min-w-0">
                      <div className="font-medium truncate">{s.title}</div>
                      <code className="text-ink-tertiary text-[10px]">{s.id}</code>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        <section className="mt-auto text-[10px] text-ink-tertiary">
          <div className="font-medium mb-1">Available tools</div>
          <ul className="flex flex-col gap-0.5">
            {Object.entries(TOOL_DESCRIPTIONS).map(([n, d]) => (
              <li key={n}>
                <code className="font-mono">{n}</code> — {d}
              </li>
            ))}
          </ul>
        </section>
      </aside>
    </div>
  )
}
